import { useEffect, useState } from 'react';
import type { KeyboardEvent, MutableRefObject, Ref, RefObject } from 'react';
import type { PickerOption } from './pickerTypes';

/** Keep both downshift hooks' input refs when composing their prop getters. */
export function mergePickerRefs(...refs: (Ref<HTMLInputElement> | undefined)[]) {
  return (node: HTMLInputElement | null) => {
    for (const ref of refs) {
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<HTMLInputElement | null>).current = node;
    }
  };
}

export function usePickerValidity(input: RefObject<HTMLInputElement>, required: boolean, hasValue: boolean) {
  useEffect(() => {
    input.current?.setCustomValidity(required && !hasValue ? 'Select an option.' : '');
  }, [input, required, hasValue]);
}

/** Wait for a settled query/state instead of announcing every input event. */
export function usePickerAnnouncement(query: string, count: number, loading: boolean,
  error: string | undefined, unavailable: string | undefined, open: boolean) {
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    if (!open && !unavailable) return;
    const timer = setTimeout(() => {
      const suffix = query ? ` for “${query}”` : '';
      setAnnouncement(unavailable ?? (loading ? 'Loading options…' : error
        ? `Could not load options. ${error}` : count === 0 ? `No matches${suffix}.`
          : `${count} result${count === 1 ? '' : 's'} available${suffix}.`));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, count, loading, error, unavailable, open]);
  return announcement;
}

/** Run downshift first, then suppress native submission only for open selection. */
export function pickerKeyDown(event: KeyboardEvent<HTMLInputElement>, open: boolean, highlighted: number,
  composing: boolean, handler?: (event: KeyboardEvent<HTMLInputElement>) => void) {
  if (composing || event.nativeEvent.isComposing || event.keyCode === 229) {
    if (event.key === 'Enter') event.preventDefault();
    return;
  }
  // Closed-menu Enter retains native Input submission. Closed Escape must never
  // reach downshift's clear-selection transition.
  if (!open && (event.key === 'Enter' || event.key === 'Escape')) return;
  handler?.(event);
  if (event.key === 'Enter' && open && highlighted >= 0) event.preventDefault();
  if (event.key === 'Escape' && open) {
    event.preventDefault();
    event.stopPropagation();
  }
}

export function PickerOptionContent({ option }: { option: PickerOption }) {
  return <><span>{option.label}</span>{option.description && <span className="ui-picker__detail">{option.description}</span>}</>;
}

export function PickerFeedback({ id, loading, error, onRetry, unavailable, disabledReason, disabled, empty }: {
  id: string; loading: boolean; error?: string; onRetry: () => void;
  unavailable?: string; disabledReason?: string; disabled: boolean; empty: boolean;
}) {
  return <>
    {(unavailable || disabledReason) && <p id={`${id}-availability`} className="ui-field__help">{unavailable || disabledReason}</p>}
    {!unavailable && <>
      {loading ? <p className="ui-field__help">Loading options…</p> : error
        ? <div className="ui-field__errors"><p>{error}</p><button type="button" className="ui-button" disabled={disabled} onClick={onRetry}>Retry</button></div>
        : empty && <p className="ui-field__help">No matches</p>}
    </>}
  </>;
}
